import { FC } from 'react'

import { Control, Controller, FieldErrors, get } from 'react-hook-form'

import dayjs from 'src/Helpers/dayjs'
import { formatDate } from 'src/Constants/formatDate'
import { TransferwiseRequirementValuesAllowed } from 'src/Types/payments'
import { PrimaryInput } from 'src/Components/PrimaryInput'
import { PrimarySelect } from 'src/Components/PrimarySelect'
import { PrimaryDatePicker } from 'src/Components/PrimaryDatePicker'

import { setSelectOptions } from '../helpers'
import {
  DateFieldWrapper,
  ErrorText,
  FormGroup,
  RedText,
  Wrapper,
} from '../styles'

type InputType = 'radio' | 'select' | 'date' | 'text'

export type RenderInputProps = {
  handleSpecifyRequirements: (val: string, key: string) => void
  example: string
  propKey: string
  name: string
  valuesAllowed: TransferwiseRequirementValuesAllowed[] | null
  refreshRequirementsOnChange: boolean
  type: InputType
  error: FieldErrors
  control: Control<Record<string, string>, Record<string, unknown>>
}

export const RenderInput: FC<RenderInputProps> = ({
  handleSpecifyRequirements,
  type,
  name,
  propKey,
  valuesAllowed,
  refreshRequirementsOnChange,
  error,
  control,
}) => {
  if (type === 'date') {
    return (
      <Wrapper>
        <FormGroup>
          <Controller
            name={propKey}
            control={control}
            render={({ field }) => {
              const errorMessage = get(error, propKey)?.message

              return (
                <DateFieldWrapper $isError={Boolean(errorMessage)}>
                  <PrimaryDatePicker
                    ref={field.ref}
                    name={field.name}
                    data-testid={propKey}
                    placeholder={name}
                    format={formatDate.formatDateISO}
                    getPopupContainer={() => document.body}
                    value={field.value ? dayjs(field.value) : null}
                    onBlur={field.onBlur}
                    onChange={(_, dateString) =>
                      field.onChange(
                        Array.isArray(dateString) ? dateString[0] : dateString,
                      )
                    }
                  />
                  {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
                </DateFieldWrapper>
              )
            }}
          />
        </FormGroup>
      </Wrapper>
    )
  }

  if (type === 'text') {
    return (
      <Wrapper>
        <FormGroup>
          <Controller
            name={propKey}
            control={control}
            render={({ field }) => (
              <PrimaryInput
                {...field}
                data-testid={propKey}
                error={get(error, propKey)?.message}
                placeholder={name}
              />
            )}
          />
        </FormGroup>
      </Wrapper>
    )
  }

  if (type === 'radio' || type === 'select') {
    if (!valuesAllowed || valuesAllowed.length === 0) {
      return null
    }

    return (
      <Wrapper>
        <FormGroup>
          <Controller
            name={propKey}
            control={control}
            render={({ field }) => (
              <PrimarySelect
                {...field}
                data-testid={propKey}
                showSearch
                optionFilterProp='label'
                label={name}
                placeholder={name}
                hasNoPlaceholderOnFocus
                error={get(error, propKey)?.message}
                options={setSelectOptions(valuesAllowed || [])}
                onChange={(value: string) => {
                  field.onChange(value)
                  if (refreshRequirementsOnChange) {
                    handleSpecifyRequirements(value, propKey)
                  }
                }}
              />
            )}
          />
        </FormGroup>
      </Wrapper>
    )
  }

  return <RedText>Something went wrong.</RedText>
}
